import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import React from 'react';
import { Stack } from '../../../../components/Stack/Stack';

import { Button } from '../../../../components/Button/Button';
import { FormDatepicker } from '../../../../components/Form/Datepicker/FormDatepicker';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { graphql } from '../../../../generated';
import { t } from '../../../../locales/translate';
import { parseDate } from '../../../../util/datetime';

interface Props {
  productId: string;
}

const BOOK_CHARTS_DATA = graphql(`
  query ProductWithBookChartsData($id: ID!) {
    product(id: $id) {
      id

      bookChartsData {
        id
        productGroup
        gtin
        description1
        description2
        releasedAt
      }
    }
  }
`);

const UPDATE_BOOK_CHARTS_DATA = graphql(`
  mutation UpdateBookChartsData($input: UpdateBooksChartsDataInput!) {
    updateBookChartsData(input: $input) {
      product {
        id
        bookChartsData {
          id
          productGroup
          gtin
          description1
          description2
          releasedAt
        }
      }
    }
  }
`);

export function EditBookChartsData({ productId }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const { data } = useSuspenseQuery(BOOK_CHARTS_DATA, { variables: { id: productId } });
  const [update] = useMutation(UPDATE_BOOK_CHARTS_DATA);
  const { fields, onSubmit, valid, submissionStatus } = useForm<{
    productGroup?: string | null;
    gtin?: string | null;
    description1?: string | null;
    description2?: string | null;
    releasedAt?: Date | null;
  }>({
    model: {
      productGroup: data.product?.bookChartsData?.productGroup,
      gtin: data.product?.bookChartsData?.gtin,
      description1: data.product?.bookChartsData?.description1,
      description2: data.product?.bookChartsData?.description2,
      releasedAt: parseDate(data.product?.bookChartsData?.releasedAt),
    },
    validations: {
      gtin: 'required',
    },
    onSubmit: async ({ model }) => {
      await update({
        variables: {
          input: {
            productId,
            ...model,
            releasedAt: model.releasedAt || null, // if we submit undefined, the value does not get updated
          },
        },
      });
      close();
    },
  });
  return (
    <Modal
      open={open}
      title={t.model.bookCharts.edit.title}
      footer={
        <>
          <Button
            title={t.actions.cancel('')}
            onClick={() => {
              close();
            }}
          />
          <Button
            type="primary"
            title={t.actions.save('')}
            onClick={onSubmit}
            disabled={!valid}
            loading={submissionStatus === 'submitting'}
          />
        </>
      }
    >
      <Stack>
        <FormTextField
          inputId="productGroup"
          label={t.model.bookCharts.productGroup}
          description={t.model.bookCharts.descriptions.productGroup}
          {...fields.productGroup}
        />
        <FormTextField
          inputId="gtin"
          label={t.model.bookCharts.gtin}
          description={t.model.bookCharts.descriptions.gtin}
          {...fields.gtin}
        />
        <FormTextField
          inputId="description1"
          label={t.model.bookCharts.description1}
          description={t.model.bookCharts.descriptions.description1}
          {...fields.description1}
        />
        <FormTextField
          inputId="description2"
          label={t.model.bookCharts.description2}
          description={t.model.bookCharts.descriptions.description2}
          {...fields.description2}
        />
        <FormDatepicker
          name="releasedAt"
          label={t.model.bookCharts.releasedAt}
          description={t.model.bookCharts.descriptions.releasedAt}
          hourpicker
          {...fields.releasedAt}
        />
      </Stack>
    </Modal>
  );
}
